import type { FC } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import load from "load-script";

import type { DeliveryOption as DeliveryOptionType } from "@jti/commerce";
import { SelectedAddressView, WebComponent } from "@jti/commerce";
import type { ExternalShippingCarrier, ExternalShippingCarrierAddress } from "@jti/commerce-types";
import { AlertBanner } from "@jti/core-components/components/AlertBanner";
import { newrelic } from "@jti/fe-clients";
import type { CheckoutShippingStepData } from "@jti/store/slices/standardCheckoutFormSlice";
import { DEPRECATED_Loading, useFormContext, useTranslations } from "@jti/ui";

import type { CustomDeliveryOptionError } from "@checkout/components/DeliveryOptions/CustomDeliveryOption/types/CustomDeliveryOptionError";

import { CustomDeliveryOptionModal } from "./CustomDeliveryOptionModal";

import styles from "../DeliveryOptions.module.scss";

type CustomDeliveryOptionProps = {
  widgetTagName?: DeliveryOptionType["widgetTagName"];
  widgetUrl?: DeliveryOptionType["widgetUrl"];
  widgetVariant?: DeliveryOptionType["widgetVariant"];
  methodCode: DeliveryOptionType["methodCode"];
  onSelectAddress: (carrierData: ExternalShippingCarrier) => void;
};

export const validateStoreAddress = (storeAddress: ExternalShippingCarrierAddress) =>
  storeAddress.storeId &&
  storeAddress.storeName &&
  storeAddress.city &&
  storeAddress.streetName &&
  storeAddress.streetNumber &&
  storeAddress.postCode;

/**
 * @deprecated
 * use CustomShippingOptionWidget feature from commerce package instead
 */
export const CustomDeliveryOption: FC<CustomDeliveryOptionProps> = ({
  widgetTagName = "",
  widgetUrl,
  widgetVariant = "embedded",
  methodCode = "",
  onSelectAddress,
}) => {
  const { __ } = useTranslations();
  const formContext = useFormContext<CheckoutShippingStepData>();
  const externalCarrierAddress = formContext.watch("externalShippingCarrier.address");
  const customDeliveryOptionRef = useRef(null);

  const formattedShippingAddress = externalCarrierAddress ? JSON.stringify(externalCarrierAddress) : "";
  const [error, setError] = useState<CustomDeliveryOptionError | undefined>(undefined);
  const [isWidgetLoaded, setIsWidgetLoaded] = useState(!!customElements.get(widgetTagName));
  const [isLoading, setIsLoading] = useState(false);
  const { errors } = formContext.formState;

  const loadWidget = useCallback(() => {
    if (!widgetUrl) {
      return;
    }

    setIsLoading(true);

    load(widgetUrl, { async: true }, (error) => {
      setIsLoading(false);

      if (error) {
        setError({
          message: __("cart.checkout.delivery.custom.error.script_loading"),
          code: "SCRIPT_LOADING_ERROR",
        });

        newrelic.noticeError(new Error("com.delivery_options.widget.error.script_loading", { cause: error }));
        console.error("Error loading custom delivery option widget", error);
        return;
      }

      setIsWidgetLoaded(true);
    });
  }, [widgetUrl]);

  const clearMagentoValidationErrors = () => {
    handleValidationErrorStatus(false);
    formContext.clearErrors();
  };

  const handleValidationErrorStatus = (hasError: boolean) => {
    if (!hasError) {
      setError(undefined);
      return;
    }

    setError({
      message: __("cart.checkout.delivery.custom.error.address_validation"),
      code: "INVALID_STORE_ADDRESS",
    });
  };

  const handleSetAddressEvent = (event: any) => {
    const selectedAddressData: ExternalShippingCarrierAddress = event.detail;

    clearMagentoValidationErrors();
    handleValidationErrorStatus(!validateStoreAddress(selectedAddressData));

    const externalCarrierData = { address: selectedAddressData, methodCode };

    onSelectAddress(externalCarrierData);
  };

  const handleWidgetCrash = (event: any) => {
    const errorMessage = event.detail?.message || __("cart.checkout.delivery.custom.error.widget_crash");
    setError({
      message: errorMessage,
      code: "WIDGET_CRASH",
    });
  };

  const setWidgetEventListeners = useCallback(() => {
    if (!customDeliveryOptionRef.current) {
      return;
    }

    const customDeliveryOption = customDeliveryOptionRef.current as HTMLElement;

    customDeliveryOption.addEventListener("customDeliveryOption:address:set", handleSetAddressEvent);
    customDeliveryOption.addEventListener("customDeliveryOption:widget:crash", handleWidgetCrash);
  }, [customDeliveryOptionRef.current]);

  const removeWidgetEventListeners = () => {
    if (!customDeliveryOptionRef.current) {
      return;
    }

    const customDeliveryOption = customDeliveryOptionRef.current as HTMLElement;

    customDeliveryOption.removeEventListener("customDeliveryOption:address:set", handleSetAddressEvent);
    customDeliveryOption.removeEventListener("customDeliveryOption:widget:crash", handleWidgetCrash);
  };

  useEffect(() => {
    const isScriptInjected = document.querySelector(`script[src="${widgetUrl}"]`);

    if (isScriptInjected || isWidgetLoaded) {
      return;
    }

    loadWidget();
  }, [loadWidget, isWidgetLoaded, widgetUrl]);

  useEffect(() => {
    if (!isWidgetLoaded || !customDeliveryOptionRef.current) {
      return;
    }

    setWidgetEventListeners();

    return removeWidgetEventListeners;
  }, [isWidgetLoaded, setWidgetEventListeners, customDeliveryOptionRef]);

  useEffect(() => {
    if (errors.shippingAddress) {
      handleValidationErrorStatus(true);
    }
  }, [errors]);

  return (
    <>
      {error?.code && (
        <div className={styles.addressErrorMessageWrapper}>
          <AlertBanner tone="error" data-testid="widget-error" description={error.message} />
        </div>
      )}

      {widgetVariant === "embedded" && (
        <>
          {isLoading && <DEPRECATED_Loading dataTestId="custom-delivery-widget-loader" />}

          {!isLoading && error?.code !== "WIDGET_CRASH" ? (
            <WebComponent
              ref={customDeliveryOptionRef}
              tagName={widgetTagName}
              error={error ? JSON.stringify(error) : undefined}
              selected-address={formattedShippingAddress}
              data-testid="custom-delivery-option-widget-embedded"
            />
          ) : null}
        </>
      )}

      {widgetVariant === "modal" && (
        <CustomDeliveryOptionModal
          webComponentRef={customDeliveryOptionRef}
          isLoading={isLoading}
          error={error}
          selectedAddress={formattedShippingAddress}
        >
          {error?.code && error.code !== "SCRIPT_LOADING_ERROR" && (
            <div className={styles.addressError}>
              <AlertBanner tone="error" data-testid="widget-error" description={error.message} />
            </div>
          )}

          {error?.code !== "WIDGET_CRASH" && (
            <WebComponent
              ref={customDeliveryOptionRef}
              tagName={widgetTagName}
              error={error ? JSON.stringify(error) : undefined}
              selected-address={formattedShippingAddress}
              data-testid="custom-delivery-option-widget-modal"
            />
          )}
        </CustomDeliveryOptionModal>
      )}

      {externalCarrierAddress && (
        <div className={styles.selectedStore}>
          <SelectedAddressView
            title={externalCarrierAddress.storeName}
            address={{
              street: externalCarrierAddress.streetName,
              city: externalCarrierAddress.city,
              country: externalCarrierAddress.countryName,
              postCode: externalCarrierAddress.postCode,
            }}
          />
        </div>
      )}
    </>
  );
};
